/**
 * ClaimSubmissionsQueue — supplier "Claim or Confirm This Profile"
 * submissions inside the Admin Compliance Vault. Flagged "supplier
 * submission", linked to the exact slug, versioned (v1, v2 …).
 * Review requires all reviewer fields. Nothing automatic — the only
 * optional write is the Company Claimed badge (audited, reversible).
 */
import React, { useEffect, useState } from "react";
import { Inbox, X, Undo2, FileDown, Loader2 } from "lucide-react";
import api, { fmtErr } from "../api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  awaiting_email_confirmation: { bg: "#fef3c7", color: "#92400e", label: "Awaiting email confirmation" },
  in_review_queue: { bg: "#dbeafe", color: "#1e40af", label: "In review queue" },
  reviewed_approved: { bg: "#dcfce7", color: "#166534", label: "Reviewed · Approved" },
  reviewed_rejected: { bg: "#fee2e2", color: "#991b1b", label: "Reviewed · Rejected" },
  expired: { bg: "#f1f5f9", color: "#64748b", label: "Expired (unconfirmed)" },
};

function Chip({ s }) {
  const c = STATUS[s] || { bg: "#f8fafc", color: "#64748b", label: s };
  return (
    <span className="font-mono" style={{ fontSize: 10, padding: "3px 7px", background: c.bg, color: c.color, borderRadius: 2, whiteSpace: "nowrap" }}>
      {c.label}
    </span>
  );
}

export function ClaimSubmissionsQueue() {
  const [items, setItems] = useState([]);
  const [audit, setAudit] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [msg, setMsg] = useState("");

  async function load() {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/claims"),
        api.get("/admin/claims/audit", { params: { limit: 20 } }),
      ]);
      setItems(a.data.items || []);
      setAudit(b.data.items || []);
    } catch { /* non-blocking */ }
  }
  useEffect(() => { load(); }, []);

  async function revertClaimed(sid) {
    if (!window.confirm("Revert the Company Claimed badge set from this submission?")) return;
    try {
      await api.post(`/admin/claims/${sid}/revert-claimed`);
      setMsg(`Company Claimed reverted for ${sid}`);
      load();
    } catch (e) {
      setMsg(fmtErr(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <section className="mt-10 cd-card p-5" data-testid="claims-queue-panel" style={{ borderLeft: "3px solid #1e40af" }}>
      <div className="overline flex items-center gap-2">
        <Inbox size={13} /> SUPPLIER SUBMISSIONS · CLAIM OR CONFIRM ({items.length})
      </div>
      <p className="mt-2 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
        Email-confirmed submissions enter this queue. Proposed capabilities stay proposed. No status,
        publication, slug, or category changes happen automatically. Corridor Verified is granted only
        through the evidence ceremony above.
      </p>
      {msg ? <div className="mt-2 font-mono text-xs" style={{ color: "#166534" }} data-testid="claims-msg">{msg}</div> : null}

      <div className="mt-4 space-y-3">
        {items.length === 0 ? (
          <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }} data-testid="claims-empty">
            No supplier submissions yet.
          </div>
        ) : items.map((s) => (
          <div key={s.submission_id} className="cd-card p-4" data-testid={`claim-row-${s.submission_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono" style={{ fontSize: 10, padding: "3px 7px", background: "#eef2ff", color: "#3730a3", borderRadius: 2 }}>
                supplier submission
              </span>
              <span className="font-mono" style={{ fontSize: 10, padding: "3px 7px", background: "#f1f5f9", borderRadius: 2 }} data-testid={`claim-version-${s.submission_id}`}>
                v{s.version}
              </span>
              <Chip s={s.status} />
              <a href={`/m/${s.plant_slug}`} className="font-mono text-xs underline" target="_blank" rel="noreferrer" data-testid={`claim-slug-link-${s.submission_id}`}>
                {s.plant_slug}
              </a>
              <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                {s.plant_snapshot?.plant_sequence_label} · {s.plant_snapshot?.display_name}
              </span>
              <span className="ml-auto font-mono text-xs" style={{ color: "var(--ink-2)" }}>{s.submission_id} · {(s.created_at || "").slice(0, 16)}</span>
            </div>
            <div className="mt-2 font-mono text-xs space-y-1" style={{ color: "var(--ink)" }}>
              <div>
                Contact: <strong>{s.contact_name}</strong>{s.contact_role ? ` (${s.contact_role})` : ""} · {s.contact_email}{s.contact_phone ? ` · ${s.contact_phone}` : ""}
              </div>
              <div>
                Location confirmed: <strong>{s.location_confirmed}</strong>
                {s.corrections ? <> · Corrections: <em>{s.corrections}</em></> : null}
              </div>
              {(s.proposed_capabilities || []).length > 0 ? (
                <div data-testid={`claim-proposed-${s.submission_id}`}>
                  Proposed capabilities (NOT applied):{" "}
                  {s.proposed_capabilities.map((c) => (
                    <span key={c} className="cd-chip" style={{ fontSize: 10, marginRight: 4 }}>{c}</span>
                  ))}
                </div>
              ) : null}
              {s.has_document ? (
                <div data-testid={`claim-doc-${s.submission_id}`}>
                  Document: {s.document.file_name} · {(s.document.file_size / 1024).toFixed(0)} KB · {s.document.mime_type} · private{" "}
                  <a href={`${API_BASE}/admin/claims/${s.submission_id}/document`} className="underline inline-flex items-center gap-1" data-testid={`claim-doc-download-${s.submission_id}`}>
                    <FileDown size={11} /> download (admin)
                  </a>
                </div>
              ) : null}
              {s.review ? (
                <div className="mt-1 p-2" style={{ background: "#f8fafc", border: "1px dashed var(--line)" }} data-testid={`claim-review-${s.submission_id}`}>
                  Reviewed by <strong>{s.review.reviewer}</strong> · {(s.review.review_date || "").slice(0, 16)} ·{" "}
                  {s.review.decision} — {s.review.decision_reason}
                  <br />Notes: {s.review.evidence_notes} · Scope: {s.review.document_scope} · Issue: {s.review.issue_date} · Exp: {s.review.expiration_date} · Revocation: {s.review.revocation_status} · v{s.review.version}
                  {s.review.company_claimed_set ? <strong> · Company Claimed badge SET</strong> : null}
                </div>
              ) : null}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {s.status === "in_review_queue" ? (
                <button className="btn btn-primary btn-sm" onClick={() => setReviewing(s)} data-testid={`claim-review-btn-${s.submission_id}`}>
                  Review
                </button>
              ) : null}
              {s.review?.company_claimed_set ? (
                <button className="btn btn-ghost btn-sm" style={{ color: "#dc2626", borderColor: "#fca5a5" }}
                  onClick={() => revertClaimed(s.submission_id)} data-testid={`claim-revert-btn-${s.submission_id}`}>
                  <Undo2 size={12} /> Revert Company Claimed
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {audit.length > 0 ? (
        <div className="mt-5">
          <div className="overline">SUBMISSION AUDIT (LATEST {audit.length})</div>
          <div className="mt-2 space-y-0.5" data-testid="claims-audit">
            {audit.map((a) => (
              <div key={a.audit_id} className="font-mono" style={{ fontSize: 10, color: "var(--ink-2)" }}>
                {(a.at || "").slice(0, 19)} · {a.action} · {a.submission_id || "—"} · {a.plant_slug || "—"} · {a.actor}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {reviewing ? (
        <ReviewModal
          s={reviewing}
          onClose={() => setReviewing(null)}
          onDone={(m2) => { setMsg(m2); setReviewing(null); load(); }}
        />
      ) : null}
    </section>
  );
}

function ReviewModal({ s, onClose, onDone }) {
  const [f, setF] = useState({
    evidence_notes: "", decision: "approved", decision_reason: "",
    document_scope: "", issue_date: "", expiration_date: "",
    revocation_status: "not_revoked", set_company_claimed: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/claims/${s.submission_id}/review`, f);
      onDone(`Submission ${s.submission_id} ${data.status}${data.company_claimed_set ? " · Company Claimed set" : ""}`);
    } catch (e2) {
      setErr(fmtErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 300 }} />
      <div className="bg-white" style={{ position: "fixed", top: "5vh", left: 0, right: 0, margin: "0 auto", maxWidth: 620, maxHeight: "90vh", overflowY: "auto", padding: 24, zIndex: 310, border: "1px solid var(--ink)" }} data-testid="claim-review-modal">
        <button onClick={onClose} style={{ position: "absolute", top: 10, right: 10 }} data-testid="claim-review-close"><X size={16} /></button>
        <div className="overline" style={{ color: "var(--orange)" }}>REVIEW SUPPLIER SUBMISSION · {s.submission_id} · v{s.version}</div>
        <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
          {s.plant_slug} · {s.plant_snapshot?.plant_sequence_label} · {s.plant_snapshot?.display_name}
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <L label="Evidence notes *">
            <textarea className="cd-textarea w-full" rows={2} value={f.evidence_notes} onChange={(e) => up("evidence_notes", e.target.value)} required data-testid="review-evidence-notes" />
          </L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Decision *">
              <select className="cd-input w-full" value={f.decision} onChange={(e) => up("decision", e.target.value)} data-testid="review-decision">
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
              </select>
            </L>
            <L label="Revocation status *">
              <select className="cd-input w-full" value={f.revocation_status} onChange={(e) => up("revocation_status", e.target.value)} data-testid="review-revocation">
                <option value="not_revoked">Not revoked</option>
                <option value="revoked">Revoked</option>
              </select>
            </L>
          </div>
          <L label="Approval / rejection reason *">
            <input className="cd-input w-full" value={f.decision_reason} onChange={(e) => up("decision_reason", e.target.value)} required data-testid="review-reason" />
          </L>
          <L label="Document scope *">
            <input className="cd-input w-full" value={f.document_scope} onChange={(e) => up("document_scope", e.target.value)} required placeholder="e.g. Covers this exact P01 location / No document provided" data-testid="review-scope" />
          </L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Issue date *">
              <input type="date" className="cd-input w-full" value={f.issue_date} onChange={(e) => up("issue_date", e.target.value)} required data-testid="review-issue-date" />
            </L>
            <L label="Expiration date *">
              <input type="date" className="cd-input w-full" value={f.expiration_date} onChange={(e) => up("expiration_date", e.target.value)} required data-testid="review-expiration-date" />
            </L>
          </div>
          {f.decision === "approved" ? (
            <label className="flex items-center gap-2 font-mono text-xs cursor-pointer">
              <input type="checkbox" checked={f.set_company_claimed} onChange={(e) => up("set_company_claimed", e.target.checked)} data-testid="review-set-claimed" />
              Set the <strong>Company Claimed</strong> badge on this plant (audited, reversible). No other status changes.
            </label>
          ) : null}
          <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            Reviewer identity and review date are recorded automatically. This never grants Corridor
            Verified, never changes Government-listed status, publication, slug, or categories.
          </div>
          {err ? <div className="font-mono text-xs" style={{ color: "#dc2626" }} data-testid="review-error">{String(err)}</div> : null}
          <button type="submit" className="btn btn-primary w-full" disabled={busy} data-testid="review-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Record review"}
          </button>
        </form>
      </div>
    </>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default ClaimSubmissionsQueue;
